import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAuthToken, getAuthHeaders, fetchWithAuth } from '../utils/authUtils';

const FeedbackPage = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    type: 'bug',
    title: '',
    description: '',
    priority: 'medium',
    userAgent: navigator.userAgent,
    url: window.location.href
  });
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth('/api/feedbacks');
      if (response.ok) {
        const data = await response.json();
        setFeedbacks(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des signalements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('loading');
    
    try {
      const response = await fetchWithAuth('/api/feedbacks', {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          submittedBy: localStorage.getItem('username') || 'Anonyme',
          submittedAt: new Date().toISOString(),
          status: 'open'
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          type: 'bug',
          title: '',
          description: '',
          priority: 'medium',
          userAgent: navigator.userAgent,
          url: window.location.href
        });
        setShowForm(false);
        fetchFeedbacks();
        
        setTimeout(() => setSubmitStatus(null), 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du signalement:', error);
      setSubmitStatus('error');
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await fetchWithAuth(`/api/feedbacks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus })
      });
      fetchFeedbacks();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
    }
  };

  const filteredFeedbacks = feedbacks.filter(feedback => {
    if (filter === 'all') return true;
    if (filter === 'bugs') return feedback.type === 'bug';
    if (filter === 'improvements') return feedback.type === 'improvement';
    return feedback.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-yellow-100 text-yellow-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'bg-gray-100 text-gray-800';
      case 'medium': return 'bg-orange-100 text-orange-800';
      case 'high': return 'bg-red-100 text-red-800';
      case 'critical': return 'bg-red-600 text-white';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Chargement des signalements...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Signalements & Améliorations</h1>
        <p className="text-gray-600">Gérez les bugs signalés et les suggestions d'amélioration pour Parc Info</p>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-gray-800">{feedbacks.length}</div>
          <div className="text-sm text-gray-600">Total des signalements</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-yellow-600">
            {feedbacks.filter(f => f.status === 'open').length}
          </div>
          <div className="text-sm text-gray-600">En attente</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-blue-600">
            {feedbacks.filter(f => f.status === 'in-progress').length}
          </div>
          <div className="text-sm text-gray-600">En cours</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-green-600">
            {feedbacks.filter(f => f.status === 'resolved').length}
          </div>
          <div className="text-sm text-gray-600">Résolus</div>
        </div>
      </div>

      {/* Barre d'actions */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded ${filter === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              Tous
            </button>
            <button
              onClick={() => setFilter('bugs')}
              className={`px-4 py-2 rounded ${filter === 'bugs' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              Bugs
            </button>
            <button
              onClick={() => setFilter('improvements')}
              className={`px-4 py-2 rounded ${filter === 'improvements' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              Améliorations
            </button>
            <button
              onClick={() => setFilter('open')}
              className={`px-4 py-2 rounded ${filter === 'open' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              En attente
            </button>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="hoppen-btn px-4 py-2 rounded shadow hover:shadow-md transition-all"
          >
            {showForm ? 'Annuler' : '+ Nouveau signalement'}
          </button>
        </div>
      </div>

      {/* Message de succès */}
      {submitStatus === 'success' && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded">
          <p className="font-bold">Signalement envoyé avec succès !</p>
          <p>Merci pour votre contribution à l'amélioration de Parc Info.</p>
        </div>
      )}

      {/* Formulaire de signalement */}
      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Nouveau signalement</h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="bug">🐛 Bug</option>
                  <option value="improvement">💡 Amélioration</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priorité
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="low">Faible</option>
                  <option value="medium">Moyenne</option>
                  <option value="high">Haute</option>
                  <option value="critical">Critique</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titre
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Résumé court du problème ou de la suggestion"
                required
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description détaillée
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="5"
                placeholder="Décrivez en détail le problème rencontré ou votre suggestion d'amélioration..."
                required
              />
            </div>

            <div className="mt-4 text-sm text-gray-500">
              <p>Page actuelle : {formData.url}</p>
              <p>Navigateur : {formData.userAgent.split(' ').slice(0, 3).join(' ')}</p>
            </div>

            <div className="mt-6 flex gap-2">
              <button
                type="submit"
                disabled={submitStatus === 'loading'}
                className="hoppen-btn px-6 py-2 rounded shadow hover:shadow-md transition-all disabled:opacity-50"
              >
                {submitStatus === 'loading' ? 'Envoi en cours...' : 'Envoyer le signalement'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Liste des signalements */}
      <div className="space-y-4">
        {filteredFeedbacks.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            Aucun signalement trouvé
          </div>
        ) : (
          filteredFeedbacks.map((feedback) => (
            <div key={feedback.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg font-semibold">{feedback.title}</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${feedback.type === 'bug' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                      {feedback.type === 'bug' ? '🐛 Bug' : '💡 Amélioration'}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs ${getPriorityColor(feedback.priority)}`}>
                      {feedback.priority === 'critical' ? '🔥' : ''} {feedback.priority}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-2">{feedback.description}</p>
                  <div className="text-sm text-gray-500">
                    <span>Par {feedback.submittedBy}</span>
                    <span className="mx-2">•</span>
                    <span>{new Date(feedback.submittedAt).toLocaleDateString('fr-FR')}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(feedback.status)}`}>
                    {feedback.status === 'open' && 'En attente'}
                    {feedback.status === 'in-progress' && 'En cours'}
                    {feedback.status === 'resolved' && 'Résolu'}
                    {feedback.status === 'rejected' && 'Rejeté'}
                  </span>
                  <select
                    value={feedback.status}
                    onChange={(e) => updateStatus(feedback.id, e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value="open">En attente</option>
                    <option value="in-progress">En cours</option>
                    <option value="resolved">Résolu</option>
                    <option value="rejected">Rejeté</option>
                  </select>
                </div>
              </div>
              {feedback.resolution && (
                <div className="mt-4 p-3 bg-gray-50 rounded">
                  <p className="text-sm font-medium text-gray-700">Résolution :</p>
                  <p className="text-sm text-gray-600">{feedback.resolution}</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FeedbackPage;